import type { ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import type { Tone } from './badge.js'
import { Spinner } from './spinner.js'

export interface Column<Row> {
  key: string
  header: ReactNode
  render: (row: Row) => ReactNode
  /** Числовые и датные столбцы выравниваются по правому краю. */
  align?: 'left' | 'right'
  className?: string
}

/**
 * Цветная полоса слева от строки. Ставится вместо ещё одной колонки:
 * колонка «состояние» и так есть, а полоса даёт то, чего колонка не даёт, —
 * возможность найти нужные строки, не читая их. Прозрачная полоса у строк
 * без пометки нужна, чтобы текст не съезжал на два пикселя.
 */
const ROW_TONE: Record<Tone, string> = {
  neutral: 'border-l-muted',
  ok: 'border-l-accent',
  warn: 'border-l-copper',
  bad: 'border-l-crimson',
}

/**
 * Таблица списка.
 *
 * Своя, а не TanStack Table, и это осознанно на шаге 0.5: на экранах каркаса
 * нужна отрисовка строк, а не сортировка, группировка и виртуализация.
 * Подключать библиотеку под возможности, которыми не пользуешься, — значит
 * платить за них сложностью прямо сейчас. Там, где появятся диалоги
 * и заказы с десятками тысяч строк, TanStack Table встанет на своё место.
 *
 * Три состояния списка различены явно, потому что в интерфейсах их путают
 * чаще всего: «идёт первая загрузка», «пусто» и «обновляется поверх
 * показанного». Последнее — самое важное: при обновлении список не должен
 * мигать пустотой, иначе человек теряет место в нём.
 */
export function DataTable<Row>({
  rows,
  columns,
  rowKey,
  rowTone,
  isLoading = false,
  isRefreshing = false,
  empty,
  className,
}: {
  rows: Row[]
  columns: Column<Row>[]
  rowKey: (row: Row) => string
  /** Пометка строки цветом слева. Возвращает undefined, если строка обычная. */
  rowTone?: (row: Row) => Tone | undefined
  isLoading?: boolean
  isRefreshing?: boolean
  empty?: ReactNode
  className?: string
}) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-line bg-raised p-8 text-sm text-muted">
        <Spinner />
        Загружаем…
      </div>
    )
  }

  if (rows.length === 0) {
    return <>{empty}</>
  }

  return (
    <div
      className={cn(
        'overflow-x-auto rounded-lg border border-line bg-raised transition-opacity',
        isRefreshing && 'opacity-60',
        className,
      )}
      aria-busy={isRefreshing || undefined}
    >
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={column.key}
                scope="col"
                className={cn(
                  'label-xs border-b border-line px-3 py-2',
                  index === 0 && 'border-l-2 border-l-transparent',
                  column.align === 'right' ? 'text-right' : 'text-left',
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const tone = rowTone?.(row)

            return (
              <tr key={rowKey(row)} className="hover:bg-paper">
                {columns.map((column, index) => (
                  <td
                    key={column.key}
                    className={cn(
                      'border-b border-line-soft px-3 py-2 align-top',
                      index === 0 &&
                        cn('border-l-2', tone ? ROW_TONE[tone] : 'border-l-transparent'),
                      column.align === 'right' && 'text-right',
                      column.className,
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="rounded-lg border border-dashed border-line bg-raised px-6 py-10 text-center">
      <p className="text-sm font-medium text-ink">{title}</p>
      {description && <p className="mx-auto mt-1 max-w-md text-sm text-muted">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  )
}
